import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import ExtraMileHeader from '../../components/ExtraMileHeader/ExtraMileHeader';
import RotateOverlay from '../sop/components/RotateOverlay';
import { loginUser, logout } from '../../store/authSlice';
import { setBackButtonUrl } from '../../store/uiSlice';
import { ASSET, fetchProgress } from '../../utils/sopGame';
import { sopFire } from '../../utils/sopSwal';
import './login.css';

function safeRedirectUrl(url) {
  const fallback =
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '/') ||
    '/';
  if (!url || typeof url !== 'string') return fallback;
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return fallback;
  }
}

const LOGIN_BG = ASSET.img('bg.png');
const LOGIN_SHELL_STYLE = {
  backgroundImage: `url(${LOGIN_BG})`,
  '--sop-login-bg': `url(${LOGIN_BG})`,
};

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { status, user, error } = useSelector((s) => s.auth);
  const videoRef = useRef(null);
  const [checking, setChecking] = useState(false);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const otp = searchParams.get('otp');
    const demo = searchParams.get('demo');
    if (otp || demo) {
      dispatch(logout());
    }
    dispatch(loginUser());
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (status === 'succeeded' && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    if (status !== 'failed') return;
    const msg = error ? String(error) : 'Session expired';
    const redirect = safeRedirectUrl(user?.backButtonRedirect);
    const title =
      msg.includes('OTP is required') || msg.includes('No saved user')
        ? 'Login required'
        : 'Session Expired!';
    Swal.fire({
      title,
      text: msg,
      icon: 'error',
      confirmButtonText: 'Close',
      customClass: {
        confirmButton: 'hf-swal-close',
        popup: 'hf-swal-popup',
      },
      buttonsStyling: false,
    }).then(() => {
      window.location.href = redirect;
    });
  }, [status, error, user]);

  // Replay guard after auth (PHP index.php)
  useEffect(() => {
    if (status !== 'succeeded' || !user) return undefined;
    let cancelled = false;
    setChecking(true);
    (async () => {
      try {
        const prog = await fetchProgress(user);
        if (cancelled) return;
        if (prog.completed) {
          setBlocked(true);
          await sopFire({
            text: 'You have played this game already',
          });
          window.location.href =
            user?.backButtonRedirect || process.env.REACT_APP_BASE_URL || '/';
          return;
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, user]);

  const goRules = () => navigate('/rules');

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="login-shell sop-login-shell" style={LOGIN_SHELL_STYLE}>
        <ExtraMileHeader />
        <div className="sop-login-intro">
          <div className="login-loader" />
        </div>
      </div>
    );
  }

  if (status === 'succeeded' && user && !blocked) {
    return (
      <div className="login-shell sop-login-shell" style={LOGIN_SHELL_STYLE}>
        <ExtraMileHeader />
        <div className="sop-login-intro">
          <RotateOverlay />
          {checking ? (
            <div className="login-loader" />
          ) : (
            <>
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                preload="metadata"
                className="sop-login-video"
                onEnded={goRules}
              >
                <source src={ASSET.video('startvideo.mp4')} type="video/mp4" />
              </video>
              <button type="button" className="sop-login-play" onClick={goRules}>
                <img src={ASSET.img('PLAY.png')} alt="PLAY" />
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell sop-login-shell" style={LOGIN_SHELL_STYLE}>
      <ExtraMileHeader />
      <div className="sop-login-intro">
        <div className="login-loader" />
      </div>
    </div>
  );
}
